import {
    PostalAddress, Brand, DayOfWeek, GeoCoordinates, LocalBusiness, Offer, OfferCatalog, OpeningHoursSpecification,
    Organization, Person, RadioStation, Time, WithContext
} from 'schema-dts'

import Env from '@/env/env'
import { contextUrl } from '@/common/json-ld/util'
import { ContactEntry } from '@/common/models/data/dashboard/contact'
import { Staff } from '@/common/models/data/dashboard/staff/staff'
import { CollaborationEntry } from '@/common/models/data/dashboard/collaboration/collaboration-entry'
import { PriceListEntry } from '@/common/models/data/dashboard/price-list'
import { IUserBasicData } from '@/common/models/data/common/user'

import JsonLdError from '@/common/errors/json-ld-error'
import { useSharedSettingsStore } from '@/common/stores/shared-settings'

// region Organization data

const address = (): PostalAddress => {
    const settings = useSharedSettingsStore()

    return {
        '@type': 'PostalAddress',
        'streetAddress': settings.setting('contact-street'),
        'postalCode': settings.setting('contact-postal-code'),
        'addressLocality': settings.setting('contact-city'),
        'addressRegion': 'Małopolskie',
        'addressCountry': 'Polska'
    }
}

const geo = (): GeoCoordinates => {
    const settings = useSharedSettingsStore()

    return {
        '@type': 'GeoCoordinates',
        'address': address(),
        'latitude': settings.setting('contact-geo-latitude'),
        'longitude': settings.setting('contact-geo-longitude')
    }
}

const openingHoursSpecification = (day: DayOfWeek, opens: Time, closes: Time): OpeningHoursSpecification => {
    return {
        '@type': 'OpeningHoursSpecification',
        'dayOfWeek': day,
        'opens': opens,
        'closes': closes
    }
}

const organization = (): Organization => {
    const settings = useSharedSettingsStore()

    return {
        '@type': 'Organization',
        'name': 'Radio1.7',
        'url': Env.basePath,
        'logo': `${Env.basePath}/img/ld/logo.png`,
        'image': `${Env.basePath}/img/ld/logo.png`,

        'telephone': settings.setting('contact-phone'),
        'address': address()
    }
}

const localBusiness = (): LocalBusiness => {
    const settings = useSharedSettingsStore()

    return {
        '@type': 'LocalBusiness',
        'name': 'Radio1.7',
        'url': Env.basePath,
        'logo': `${Env.basePath}/img/ld/logo.png`,
        'image': `${Env.basePath}/img/ld/logo.png`,

        'priceRange': '$',

        'telephone': settings.setting('contact-phone'),
        'address': address(),
        'geo': geo()
    }
}

const radioStation = (): WithContext<RadioStation> => {
    const settings = useSharedSettingsStore()

    return {
        '@context': contextUrl,
        '@type': 'RadioStation',
        'name': 'Radio1.7',
        'description': 'Realizujemy koncerty na żywo, wywiady z wykładowcami, artystami i osobami publicznymi. Prowadzimy imprezy, takie jak Juwenalia AGH czy koncerty okolicznościowe, piszemy także recenzje płyt i relacje z koncertów. Informacje o wszystkich wydarzeniach tylko w Radio1.7',
        'url': Env.basePath,
        'logo': `${Env.basePath}/img/ld/logo.png`,
        'image': `${Env.basePath}/img/ld/logo.png`,

        'email': settings.setting('contact-mail'),
        'telephone': settings.setting('contact-phone'),

        'currenciesAccepted': ['PLN', 'EUR'],
        'paymentAccepted': 'Cash',
        'priceRange': '$',

        'address': address(),
        'geo': geo(),

        'openingHoursSpecification': [
            openingHoursSpecification('Monday', '10:00:00', '22:00:00'),
            openingHoursSpecification('Tuesday', '10:00:00', '22:00:00'),
            openingHoursSpecification('Wednesday', '10:00:00', '22:00:00'),
            openingHoursSpecification('Thursday', '10:00:00', '22:00:00'),
            openingHoursSpecification('Friday', '10:00:00', '22:00:00'),
            openingHoursSpecification('Saturday', '15:00:00', '22:00:00'),
            openingHoursSpecification('Sunday', '15:00:00', '22:00:00')
        ]
    }
}

// endregion

// region Price List

const priceListEntry = (entry: PriceListEntry): Offer => {
    return {
        '@type': 'Offer',
        'itemOffered': {
            '@type': 'Service',
            'name': entry.service
        },
        'description': entry.description,
        'price': `${entry.price} PLN`,
        'priceCurrency': 'PLN'
    }
}

const priceList = (entries: Array<PriceListEntry>): WithContext<OfferCatalog> => {
    const elements: Array<Offer> = []

    entries.forEach(entry => {
        if (!entry.header) {
            elements.push(priceListEntry(entry))
        }
    })

    return {
        '@context': contextUrl,
        '@type': 'OfferCatalog',
        'numberOfItems': entries.length,
        'itemListElement': elements
    }
}

// endregion

// region Contact data

const contactEntry = (entry: ContactEntry): WithContext<Person> => {
    if (!entry.staff.user) {
        throw new JsonLdError('entry.staff.user is required')
    }

    return {
        '@context': contextUrl,
        '@type': 'Person',
        'name': entry.staff.user.name,
        'image': entry.staff.photo,
        'telephone': entry.phone,
        'email': entry.email,
        'hasOccupation': {
            '@type': 'Occupation',
            'name': entry.role
        }
    }
}

const staffData = (entry: Staff | IUserBasicData): WithContext<Person> => {
    if (entry instanceof Staff) {
        return staffDataFromStaff(entry)
    } else {
        return staffDataFromUser(entry)
    }
}

const staffDataFromUser = (entry: IUserBasicData): WithContext<Person> => {
    if (!entry.staff || !entry.staff[0]) {
        throw new JsonLdError('entry.staff is required')
    }

    const staff = entry.staff[0]

    const data: WithContext<Person> = {
        '@context': contextUrl,
        '@type': 'Person',
        'name': entry.name,
        'description': staff.description,
        'image': staff.photo
    }

    if (staff.radio_role) {
        data.hasOccupation = {
            '@type': 'Occupation',
            'name': staff.radio_role.role
        }
    }

    return data
}

const staffDataFromStaff = (entry: Staff): WithContext<Person> => {
    if (!entry.user) {
        throw new JsonLdError('entry.user is required')
    }

    const data: WithContext<Person> = {
        '@context': contextUrl,
        '@type': 'Person',
        'name': entry.user.name,
        'description': entry.description,
        'image': entry.photo
    }

    if (entry.radio_role) {
        data.hasOccupation = {
            '@type': 'Occupation',
            'name': entry.radio_role.role
        }
    }

    return data
}

const collaborationEntry = (entry: CollaborationEntry): WithContext<Brand> => {
    return {
        '@context': contextUrl,
        '@type': 'Brand',
        'name': entry.name,
        'url': entry.link,
        'logo': entry.logo
    }
}

// endregion

export {
    contactEntry, collaborationEntry,
    localBusiness, organization, radioStation,
    priceList,
    staffData
}
